import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, Save, X, Home, TrendingUp, Users, CheckCircle, Clock, AlertCircle, Bell } from 'lucide-react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_canvas-demo/artifacts/c288di76_28201.png';

const STATUS_CONFIG = {
  nuevo: { label: 'Nuevo', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  contactado: { label: 'Contactado', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  negociacion: { label: 'Negociación', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  convertido: { label: 'Convertido', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  perdido: { label: 'Perdido', color: 'bg-red-500/20 text-red-400 border-red-500/30' }
};

export default function AdminPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [messages, setMessages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [pendingFollowUpLeads, setPendingFollowUpLeads] = useState([]);
  const [leadsStats, setLeadsStats] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [activeTab, setActiveTab] = useState('leads');
  const [selectedLead, setSelectedLead] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [showPendingAlert, setShowPendingAlert] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'web',
    project_url: '',
    image_url: '',
    video_url: '',
    featured: false
  });

  useEffect(() => {
    fetchProjects();
    fetchMessages();
    fetchLeads();
    fetchLeadsStats();
    fetchPendingFollowUp();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/portfolio`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/contact`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  };

  const fetchLeadsStats = async () => {
    try {
      const response = await axios.get(`${API}/leads/stats`);
      setLeadsStats(response.data);
    } catch (error) {
      console.error('Error fetching leads stats:', error);
    }
  };

  const fetchPendingFollowUp = async () => {
    try {
      const response = await axios.get(`${API}/leads/pending-follow-up`);
      setPendingFollowUpLeads(response.data);
    } catch (error) {
      console.error('Error fetching pending follow up:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingProject) {
        await axios.put(`${API}/portfolio/${editingProject.id}`, formData);
        toast.success('Proyecto actualizado exitosamente');
      } else {
        await axios.post(`${API}/portfolio`, formData);
        toast.success('Proyecto creado exitosamente');
      }
      
      resetForm();
      fetchProjects();
    } catch (error) {
      toast.error('Error al guardar proyecto');
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      description: project.description,
      category: project.category,
      project_url: project.project_url || '',
      image_url: project.image_url || '',
      video_url: project.video_url || '',
      featured: project.featured
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este proyecto?')) {
      try {
        await axios.delete(`${API}/portfolio/${id}`);
        toast.success('Proyecto eliminado');
        fetchProjects();
      } catch (error) {
        toast.error('Error al eliminar proyecto');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'web',
      project_url: '',
      image_url: '',
      video_url: '',
      featured: false
    });
    setEditingProject(null);
    setShowForm(false);
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/leads/${leadId}/status`, { status: newStatus });
      toast.success('Estado actualizado');
      fetchLeads();
      fetchLeadsStats();
      fetchPendingFollowUp();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleAddNote = async (leadId) => {
    if (!newNote.trim()) return;
    
    try {
      await axios.post(`${API}/leads/${leadId}/notes`, { note: newNote });
      toast.success('Nota agregada');
      setNewNote('');
      fetchLeads();
      fetchPendingFollowUp();
      const updatedLead = leads.find(l => l.id === leadId);
      if (updatedLead) {
        const response = await axios.get(`${API}/leads/${leadId}`);
        setSelectedLead(response.data);
      }
    } catch (error) {
      toast.error('Error al agregar nota');
    }
  };

  const getFollowUpBadge = (days) => {
    if (days >= 7) {
      return { text: `${days} días sin seguimiento`, color: 'bg-red-500/20 text-red-400 border-red-500/30' };
    } else if (days >= 3) {
      return { text: `${days} días sin seguimiento`, color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' };
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" richColors />
      
      <div className="glass border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Logo" className="h-12" data-testid="admin-logo" />
              <h1 className="text-2xl font-bold text-foreground" data-testid="admin-title">Panel Administrativo</h1>
            </div>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-foreground hover:text-primary font-medium"
              data-testid="back-to-home"
            >
              <Home className="w-5 h-5" />
              Volver al Sitio
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setActiveTab('leads')}
            className={`relative px-6 py-3 rounded-md font-bold uppercase tracking-wider transition-all duration-300 ${
              activeTab === 'leads'
                ? 'bg-primary text-primary-foreground shadow-glow'
                : 'bg-background-secondary text-foreground hover:bg-background-tertiary'
            }`}
            data-testid="tab-leads"
          >
            Leads ({leads.length})
            {leadsStats && leadsStats.pending_follow_up > 0 && (
              <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="pending-badge">
                {leadsStats.pending_follow_up}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('projects')}
            className={`px-6 py-3 rounded-md font-bold uppercase tracking-wider transition-all duration-300 ${
              activeTab === 'projects'
                ? 'bg-primary text-primary-foreground shadow-glow'
                : 'bg-background-secondary text-foreground hover:bg-background-tertiary'
            }`}
            data-testid="tab-projects"
          >
            Proyectos ({projects.length})
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            className={`px-6 py-3 rounded-md font-bold uppercase tracking-wider transition-all duration-300 ${
              activeTab === 'messages'
                ? 'bg-primary text-primary-foreground shadow-glow'
                : 'bg-background-secondary text-foreground hover:bg-background-tertiary'
            }`}
            data-testid="tab-messages"
          >
            Mensajes ({messages.length})
          </button>
        </div>

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-6" data-testid="leads-heading">Sistema de Gestión de Leads</h2>
            
            {/* Pending Follow-up Alert */}
            <AnimatePresence>
              {leadsStats && leadsStats.pending_follow_up > 0 && showPendingAlert && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="mb-6 bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/30 rounded-xl p-6"
                  data-testid="pending-alert"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Bell className="w-6 h-6 text-red-400 animate-pulse" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-foreground mb-2">
                          ¡Atención! {leadsStats.pending_follow_up} {leadsStats.pending_follow_up === 1 ? 'lead necesita' : 'leads necesitan'} seguimiento
                        </h3>
                        <p className="text-foreground-muted mb-4">
                          Hay leads que llevan 3 o más días sin contacto. Es importante hacer seguimiento para no perder oportunidades.
                        </p>
                        <div className="space-y-2">
                          {pendingFollowUpLeads.slice(0, 3).map((lead) => (
                            <div key={lead.id} className="flex items-center gap-3 text-sm">
                              <Clock className="w-4 h-4 text-yellow-400" />
                              <span className="text-foreground font-medium">{lead.name}</span>
                              <span className="text-foreground-muted">• {lead.days_without_follow_up} días sin seguimiento</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowPendingAlert(false)}
                      className="text-foreground-muted hover:text-foreground"
                      data-testid="close-alert"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {leadsStats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="glass rounded-xl p-6" data-testid="stat-total">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-foreground-muted text-sm">Total Leads</p>
                      <p className="text-3xl font-bold text-foreground">{leadsStats.total}</p>
                    </div>
                  </div>
                </div>
                
                <div className="glass rounded-xl p-6" data-testid="stat-nuevos">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-foreground-muted text-sm">Nuevos</p>
                      <p className="text-3xl font-bold text-foreground">{leadsStats.nuevos}</p>
                    </div>
                  </div>
                </div>
                
                <div className="glass rounded-xl p-6" data-testid="stat-convertidos">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <p className="text-foreground-muted text-sm">Convertidos</p>
                      <p className="text-3xl font-bold text-foreground">{leadsStats.convertidos}</p>
                    </div>
                  </div>
                </div>
                
                <div className="glass rounded-xl p-6" data-testid="stat-conversion">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-foreground-muted text-sm">Tasa Conversión</p>
                      <p className="text-3xl font-bold text-foreground">{leadsStats.conversion_rate}%</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {leads.length === 0 ? (
              <div className="text-center py-20 glass rounded-xl">
                <p className="text-foreground-muted text-xl" data-testid="no-leads">No hay leads aún</p>
              </div>
            ) : (
              <div className="space-y-4">
                {leads.map((lead) => {
                  const followUpBadge = getFollowUpBadge(lead.days_without_follow_up);
                  return (
                    <motion.div
                      key={lead.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`glass rounded-xl p-6 ${
                        lead.days_without_follow_up >= 3 ? 'border-2 border-yellow-500/30' : ''
                      }`}
                      data-testid={`lead-${lead.id}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="text-lg font-bold text-foreground">{lead.name}</h3>
                            <select
                              value={lead.status}
                              onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                              className={`px-3 py-1 rounded-md text-sm font-medium border ${STATUS_CONFIG[lead.status].color}`}
                              data-testid={`status-select-${lead.id}`}
                            >
                              <option value="nuevo">Nuevo</option>
                              <option value="contactado">Contactado</option>
                              <option value="negociacion">Negociación</option>
                              <option value="convertido">Convertido</option>
                              <option value="perdido">Perdido</option>
                            </select>
                            {followUpBadge && (
                              <span className={`px-3 py-1 rounded-md text-xs font-medium border flex items-center gap-1 ${followUpBadge.color}`} data-testid={`follow-up-badge-${lead.id}`}>
                                <Clock className="w-3 h-3" />
                                {followUpBadge.text}
                              </span>
                            )}
                          </div>
                          <p className="text-foreground-muted text-sm">{lead.email} {lead.phone && `• ${lead.phone}`}</p>
                          <p className="text-foreground mt-2">{lead.message}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-foreground-muted">
                            {new Date(lead.created_at).toLocaleDateString('es-ES', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>

                      {selectedLead?.id === lead.id && (
                        <div className="mt-4 pt-4 border-t border-border">
                          <h4 className="text-sm font-bold text-foreground mb-3">Notas de Seguimiento</h4>
                          
                          {lead.notes && lead.notes.length > 0 && (
                            <div className="space-y-2 mb-4">
                              {lead.notes.map((note, idx) => (
                                <div key={idx} className="bg-background-tertiary p-3 rounded-md">
                                  <p className="text-foreground text-sm">{note.note}</p>
                                  <p className="text-xs text-foreground-muted mt-1">
                                    {new Date(note.created_at).toLocaleString('es-ES')}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newNote}
                              onChange={(e) => setNewNote(e.target.value)}
                              placeholder="Agregar nota de seguimiento..."
                              className="flex-1 bg-[#051114] border border-[#1a4d5c] focus:border-primary focus:ring-1 focus:ring-primary/50 rounded-md px-4 py-2 text-foreground text-sm"
                              data-testid={`note-input-${lead.id}`}
                            />
                            <button
                              onClick={() => handleAddNote(lead.id)}
                              className="bg-primary text-primary-foreground px-4 py-2 rounded-md font-medium hover:bg-primary/90 transition-colors"
                              data-testid={`add-note-${lead.id}`}
                            >
                              Agregar
                            </button>
                          </div>
                        </div>
                      )}

                      <button
                        onClick={() => setSelectedLead(selectedLead?.id === lead.id ? null : lead)}
                        className="text-primary hover:text-primary/80 text-sm font-medium mt-3"
                        data-testid={`toggle-notes-${lead.id}`}
                      >
                        {selectedLead?.id === lead.id ? 'Ocultar notas' : 'Ver/Agregar notas'}
                      </button>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-foreground" data-testid="projects-heading">Gestionar Proyectos</h2>
              {!showForm && (
                <button
                  onClick={() => setShowForm(true)}
                  className="flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3 rounded-md font-bold uppercase tracking-wider shadow-glow transition-all duration-300"
                  data-testid="add-project-button"
                >
                  <Plus className="w-5 h-5" />
                  Agregar Proyecto
                </button>
              )}
            </div>

            {showForm && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-heavy rounded-xl p-8 mb-8"
              >
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-foreground" data-testid="form-title">
                    {editingProject ? 'Editar Proyecto' : 'Nuevo Proyecto'}
                  </h3>
                  <button
                    onClick={resetForm}
                    className="text-foreground-muted hover:text-foreground"
                    data-testid="close-form-button"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="project-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-foreground font-medium mb-2">Título</label>
                      <input
                        type="text"
                        required
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        className="w-full bg-[#051114] border border-[#1a4d5c] focus:border-primary focus:ring-1 focus:ring-primary/50 rounded-md px-4 py-3 text-foreground"
                        data-testid="project-title-input"
                      />
                    </div>

                    <div>
                      <label className="block text-foreground font-medium mb-2">Categoría</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        className="w-full bg-[#051114] border border-[#1a4d5c] focus:border-primary focus:ring-1 focus:ring-primary/50 rounded-md px-4 py-3 text-foreground"
                        data-testid="project-category-select"
                      >
                        <option value="web">Página Web</option>
                        <option value="video">Video Animado</option>
                        <option value="diseño">Diseño Gráfico</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-foreground font-medium mb-2">Descripción</label>
                    <textarea
                      required
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full bg-[#051114] border border-[#1a4d5c] focus:border-primary focus:ring-1 focus:ring-primary/50 rounded-md px-4 py-3 text-foreground"
                      data-testid="project-description-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-foreground font-medium mb-2">URL del Proyecto</label>
                      <input
                        type="url"
                        value={formData.project_url}
                        onChange={(e) => setFormData({...formData, project_url: e.target.value})}
                        className="w-full bg-[#051114] border border-[#1a4d5c] focus:border-primary focus:ring-1 focus:ring-primary/50 rounded-md px-4 py-3 text-foreground"
                        placeholder="https://..."
                        data-testid="project-url-input"
                      />
                    </div>

                    <div>
                      <label className="block text-foreground font-medium mb-2">URL de Imagen</label>
                      <input
                        type="url"
                        value={formData.image_url}
                        onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                        className="w-full bg-[#051114] border border-[#1a4d5c] focus:border-primary focus:ring-1 focus:ring-primary/50 rounded-md px-4 py-3 text-foreground"
                        placeholder="https://..."
                        data-testid="project-image-input"
                      />
                    </div>

                    <div>
                      <label className="block text-foreground font-medium mb-2">URL de Video</label>
                      <input
                        type="url"
                        value={formData.video_url}
                        onChange={(e) => setFormData({...formData, video_url: e.target.value})}
                        className="w-full bg-[#051114] border border-[#1a4d5c] focus:border-primary focus:ring-1 focus:ring-primary/50 rounded-md px-4 py-3 text-foreground"
                        placeholder="https://..."
                        data-testid="project-video-input"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="featured"
                      checked={formData.featured}
                      onChange={(e) => setFormData({...formData, featured: e.target.checked})}
                      className="w-5 h-5 rounded border-[#1a4d5c] bg-[#051114] text-primary focus:ring-primary"
                      data-testid="project-featured-checkbox"
                    />
                    <label htmlFor="featured" className="text-foreground font-medium">Proyecto Destacado</label>
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="submit"
                      className="flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3 rounded-md font-bold uppercase tracking-wider shadow-glow transition-all duration-300"
                      data-testid="save-project-button"
                    >
                      <Save className="w-5 h-5" />
                      {editingProject ? 'Actualizar' : 'Guardar'}
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="bg-transparent border border-border text-foreground hover:bg-background-tertiary px-6 py-3 rounded-md font-bold uppercase tracking-wider transition-all duration-300"
                      data-testid="cancel-project-button"
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass rounded-xl overflow-hidden"
                  data-testid={`admin-project-${project.id}`}
                >
                  {project.image_url && (
                    <img src={project.image_url} alt={project.title} className="w-full h-48 object-cover" />
                  )}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <span className="text-xs font-medium tracking-widest uppercase text-primary">{project.category}</span>
                        {project.featured && <span className="ml-2 text-xs bg-primary/20 text-primary px-2 py-1 rounded">Destacado</span>}
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-foreground mb-2">{project.title}</h3>
                    <p className="text-foreground-muted text-sm mb-4 line-clamp-2">{project.description}</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(project)}
                        className="flex-1 flex items-center justify-center gap-2 bg-background-tertiary hover:bg-secondary text-foreground px-4 py-2 rounded-md transition-colors"
                        data-testid={`edit-project-${project.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(project.id)}
                        className="flex items-center justify-center gap-2 bg-status-error/10 hover:bg-status-error/20 text-status-error px-4 py-2 rounded-md transition-colors"
                        data-testid={`delete-project-${project.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {projects.length === 0 && !showForm && (
              <div className="text-center py-20 glass rounded-xl">
                <p className="text-foreground-muted text-xl mb-4" data-testid="no-projects-admin">No hay proyectos creados</p>
                <button
                  onClick={() => setShowForm(true)}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3 rounded-md font-bold uppercase tracking-wider shadow-glow transition-all duration-300"
                >
                  Crear Primer Proyecto
                </button>
              </div>
            )}
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-6" data-testid="messages-heading">Mensajes de Contacto</h2>
            
            {messages.length === 0 ? (
              <div className="text-center py-20 glass rounded-xl">
                <p className="text-foreground-muted text-xl" data-testid="no-messages">No hay mensajes aún</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass rounded-xl p-6"
                    data-testid={`message-${message.id}`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-foreground">{message.name}</h3>
                        <p className="text-foreground-muted text-sm">{message.email} {message.phone && `• ${message.phone}`}</p>
                      </div>
                      <span className="text-xs text-foreground-muted">
                        {new Date(message.created_at).toLocaleDateString('es-ES', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="text-foreground">{message.message}</p>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}